import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as evtApi from "../api/events";
import * as commApi from "../api/community"; // for community dropdown
import bgImg from "../assets/1.png";

function useDebouncedValue(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
  return v;
}

export default function EventsList() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [communities, setCommunities] = useState([]);

  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 350);

  const [communityId, setCommunityId] = useState("");
  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");

  const [sortBy, setSortBy] = useState("created_at");
  const [order, setOrder] = useState("desc");

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);

  const navigate = useNavigate();
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    (async () => {
      try {
        const cats = await evtApi.listCategories();
        setCategories(Array.isArray(cats) ? cats : []);
      } catch { setCategories([]); }
      try {
        const list = await commApi.listCommunities();
        const arr = Array.isArray(list) ? list : (list?.rows ?? []);
        setCommunities(arr);
      } catch { setCommunities([]); }
    })();
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    setSubCategory("");
    if (!category) { setSubCategories([]); return; }
    (async () => {
      try {
        const subs = await evtApi.listSubCategories({ categoryName: category });
        setSubCategories(Array.isArray(subs) ? subs : []);
      } catch { setSubCategories([]); }
    })();
  }, [category]);

  const query = useMemo(() => ({
    q: qDebounced || undefined,
    communityId: communityId || undefined,
    category: category || undefined,
    subCategory: subCategory || undefined,
    createdFrom: createdFrom || undefined,
    createdTo: createdTo || undefined,
    sortBy, order,
    limit, offset: (page - 1) * limit,
    paged: true
  }), [qDebounced, communityId, category, subCategory, createdFrom, createdTo, sortBy, order, limit, page]);

  useEffect(() => {
    (async () => {
      setLoading(true); setErrMsg("");
      try {
        const resp = await evtApi.listEventsPaged(query);
        if (!mounted.current) return;
        const arr = Array.isArray(resp) ? resp : (resp?.rows ?? []);
        setRows(Array.isArray(arr) ? arr : []);
        setTotal(Number(resp?.total ?? arr.length) || 0);
        const lim = Number(resp?.limit) || limit;
        if (lim !== limit) setLimit(lim);
      } catch (e) {
        if (!mounted.current) return;
        setErrMsg(e?.response?.data?.error || "Failed to load events");
        setRows([]); setTotal(0);
      } finally { if (mounted.current) setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, limit)));

  const handleEdit = id => navigate(`/events/${id}/edit`);
  const handleDelete = async id => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await evtApi.deleteEvent(id);
      setRows(prev => prev.filter(r => r.id !== id));
      setTotal(t => Math.max(0, t - 1));
    } catch (e) { alert(e?.response?.data?.error || "Delete failed"); }
  };
  const handleAdd = () => navigate("/events/new");

  const setSort = (col) => {
    if (sortBy === col) setOrder(o => (o === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setOrder("asc"); }
  };

  const resetFilters = () => {
    setQ(""); setCommunityId(""); setCategory(""); setSubCategory("");
    setCreatedFrom(""); setCreatedTo(""); setSortBy("created_at"); setOrder("desc");
    setLimit(10); setPage(1);
  };

  return (
    <section className="cl-section">
      <style>{`
        .cl-section { min-height:100vh; display:flex; justify-content:center; padding:1rem;
          background-image: url(${bgImg}), linear-gradient(135deg, rgba(210,168,87,0.7), rgba(139,186,161,0.7), rgba(5,102,117,0.7)); background-size:cover; background-position:center; }
        .cl-card { width:100%; max-width:72rem; background:#ffffffc7; border-radius:1rem; box-shadow:0 10px 25px rgba(0,0,0,.08); padding:1.25rem; }
        .cl-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:1rem; gap:1rem; flex-wrap:wrap; }
        .cl-title { font-size:1.5rem; font-weight:900; color:#7A133D; }
        .add-btn { background:#D2A857; color:#fff; border:none; border-radius:.5rem; padding:.45rem .9rem; font-weight:700; cursor:pointer; }
        .filters { display:grid; grid-template-columns: repeat(6, minmax(0,1fr)); gap:.5rem; margin-bottom:1rem; }
        .filters .full { grid-column:1 / -1; }
        .filters input, .filters select { padding:.45rem .55rem; border:1px solid rgba(0,0,0,.15); border-radius:.5rem; }
        .reset { background:#fff; border:1px solid #C62039; color:#C62039; border-radius:.5rem; padding:.45rem .6rem; font-weight:700; cursor:pointer; }
        .cl-table-wrapper { overflow-x:auto; }
        table { width:100%; border-collapse:collapse; background:#fff; border-radius:.75rem; overflow:hidden; }
        thead th { background:#D2A857; color:#fff; padding:.55rem; text-align:left; white-space:nowrap; user-select:none; }
        thead th.sortable { cursor:pointer; }
        tbody td { padding:.55rem; border-bottom:1px solid rgba(0,0,0,.05); }
        .cl-actions { display:flex; gap:.5rem; }
        .cl-btn { border:none; border-radius:.35rem; padding:.25rem .5rem; font-weight:600; cursor:pointer; }
        .cl-btn.edit { background:#D2A857; color:#fff; }
        .cl-btn.delete { background:#C62039; color:#fff; }
        .err { color:#b3261e; background:#fdecea; border:1px solid #f5c2c0; padding:.5rem .75rem; border-radius:.5rem; margin-bottom:.75rem; }
        .loading { font-size:.95rem; opacity:.8; padding:.25rem 0; }
        @media (max-width:920px){ .filters{ grid-template-columns:repeat(2,minmax(0,1fr)); } }
      `}</style>

      <div className="cl-card">
        <div className="cl-header">
          <h2 className="cl-title">Events</h2>
          <button className="add-btn" onClick={handleAdd}>+ Add New</button>
        </div>

        <div className="filters">
          <input className="full" type="search" placeholder="Search name, description, address, email, in-charge…"
                 value={q} onChange={e => { setQ(e.target.value); setPage(1); }} />
          <select value={communityId} onChange={e => { setCommunityId(e.target.value); setPage(1); }}>
            <option value="">All Communities</option>
            {(communities ?? []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={category} onChange={e => { setCategory(e.target.value); setPage(1); }}>
            <option value="">All Categories</option>
            {(categories ?? []).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          <select value={subCategory} onChange={e => { setSubCategory(e.target.value); setPage(1); }} disabled={!category}>
            <option value="">{category ? 'All Sub-categories' : 'Choose category first'}</option>
            {(subCategories ?? []).map(sc => <option key={sc.id} value={sc.name}>{sc.name}</option>)}
          </select>
          <input type="date" value={createdFrom} onChange={e => { setCreatedFrom(e.target.value); setPage(1); }} />
          <input type="date" value={createdTo}   onChange={e => { setCreatedTo(e.target.value);   setPage(1); }} />
          <button className="reset" onClick={resetFilters}>Reset</button>
        </div>

        {errMsg && <div className="err">{errMsg}</div>}
        {loading && <div className="loading">Loading…</div>}

        <div className="cl-table-wrapper">
          <table>
            <thead>
              <tr>
                <th className="sortable" onClick={() => setSort("name")}>Name {sortBy==='name'?(order==='asc'?'▲':'▼'):''}</th>
                <th className="sortable" onClick={() => setSort("community_name")}>Community {sortBy==='community_name'?(order==='asc'?'▲':'▼'):''}</th>
                <th className="sortable" onClick={() => setSort("category")}>Category {sortBy==='category'?(order==='asc'?'▲':'▼'):''}</th>
                <th>Sub-category</th>
                <th className="sortable" onClick={() => setSort("created_at")}>Created {sortBy==='created_at'?(order==='asc'?'▲':'▼'):''}</th>
                <th style={{ width: 140 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(rows ?? []).map(r => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td>{r.community_name}</td>
                  <td>{r.category}</td>
                  <td>{r.sub_category}</td>
                  <td>{r.created_at ? new Date(r.created_at).toLocaleString() : '-'}</td>
                  <td>
                    <div className="cl-actions">
                      <button className="cl-btn edit"   onClick={() => handleEdit(r.id)}>Edit</button>
                      <button className="cl-btn delete" onClick={() => handleDelete(r.id)}>Del</button>
                    </div>
                  </td>
                </tr>
              ))}
              {(rows ?? []).length === 0 && !loading && (
                <tr><td colSpan="6" style={{ textAlign:'center', padding:'1rem' }}>No events found.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* pagination */}
        <div style={{ display:'flex', justifyContent:'space-between', marginTop:'.75rem', gap:'.75rem', flexWrap:'wrap' }}>
          <div>{total>0 ? <>Showing <b>{(page-1)*limit+1}</b>–<b>{Math.min(page*limit,total)}</b> of <b>{total}</b></> : 'No results'}</div>
          <div style={{ display:'flex', gap:'.4rem' }}>
            <button onClick={() => setPage(1)} disabled={page<=1}>⏮ First</button>
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page<=1}>◀ Prev</button>
            <span style={{ padding:'.35rem .6rem' }}>Page {page} / {Math.max(1, Math.ceil(total/Math.max(1,limit)))}</span>
            <button onClick={() => setPage(p => Math.min(Math.max(1, Math.ceil(total/Math.max(1,limit))), p+1))}
                    disabled={page >= Math.max(1, Math.ceil(total/Math.max(1,limit)))}>Next ▶</button>
            <button onClick={() => setPage(Math.max(1, Math.ceil(total/Math.max(1,limit))))}
                    disabled={page >= Math.max(1, Math.ceil(total/Math.max(1,limit)))}>Last ⏭</button>
            <select value={limit} onChange={e => { setLimit(parseInt(e.target.value,10)||10); setPage(1); }}>
              {[10,20,50,100].map(n => <option key={n} value={n}>{n}/page</option>)}
            </select>
          </div>
        </div>
      </div>
    </section>
  );
}
